'use client'

import { safeTranslateColor } from '@/lib/helpers/sanitize-i18n-keys'
import { stringToSlug } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { usePathname, useSearchParams } from 'next/navigation'
import { useMemo, useState } from 'react'
import { useProductDetailSafe } from '../../context/product-detail-context'
import { useProductDetailStore } from '../../hooks/use-product-detail-store'
import { ProductInfoVariantItem } from '../product-info/product-info-variant-item'

export const InfoBarColorShort = () => {
  const { colorOptionActive, sortedColorOptions, scrollToFirstImageRef } =
    useProductDetailSafe()
  const { handleChangeColor } = useProductDetailStore()
  const [isExpanded, setIsExpanded] = useState(false)
  const t = useTranslations()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Build URL với query parameter color cho mỗi variant
  const buildColorUrl = useMemo(() => {
    return (colorSlug: string) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set('color', colorSlug)
      return `${pathname}?${params.toString()}`
    }
  }, [pathname, searchParams])

  const displayedVariants = isExpanded
    ? sortedColorOptions
    : sortedColorOptions.slice(0, 4)

  if (!sortedColorOptions.length) return null

  return (
    <div className='border-l border-l-neutral-200 py-4 pl-2 2xl:pl-6'>
      <p className='mb-1 text-sm leading-4.5 text-neutral-600'>
        {t('title.color')}:
        <span className='ml-3 font-semibold text-neutral-900'>
          {' '}
          {safeTranslateColor(t, colorOptionActive?.title || '')}
        </span>
      </p>
      <div className='max-w-[380px] overflow-hidden'>
        <ul className='grid grid-cols-6 gap-2 gap-y-3 2xl:grid-cols-4'>
          {displayedVariants.map((variant) => {
            const colorSlug = stringToSlug(variant.title)
            const colorUrl = buildColorUrl(colorSlug)
            return (
              <li key={variant.title} className='variant-item'>
                <ProductInfoVariantItem
                  isActive={colorOptionActive?.title === variant.title}
                  onClick={() => {
                    handleChangeColor(variant)
                    scrollToFirstImageRef.current?.()
                  }}
                  href={colorUrl}
                  title={variant.title}
                  imageUrl={variant?.image?.[variant?.image?.length - 1]?.src}
                />
              </li>
            )
          })}
        </ul>
      </div>

      {sortedColorOptions.length > 5 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className='mt-1 font-sans text-sm text-neutral-500 underline'
        >
          {isExpanded
            ? t('button.collapse')
            : t('product_detail.title.view_all_color')}
        </button>
      )}

      <style jsx>{`
        .variant-item {
          opacity: 0;
          transform: translateY(20px);
          animation: slideIn 0.2s ease forwards;
        }

        @keyframes slideIn {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .variant-item:nth-child(1) {
          animation-delay: 0.03s;
        }
        .variant-item:nth-child(2) {
          animation-delay: 0.06s;
        }
        .variant-item:nth-child(3) {
          animation-delay: 0.09s;
        }
        .variant-item:nth-child(4) {
          animation-delay: 0.12s;
        }
        .variant-item:nth-child(5) {
          animation-delay: 0.15s;
        }
      `}</style>
    </div>
  )
}
